import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  api,
  COMPANY_LABEL,
  STATUS_LABEL,
  type Customer,
  type CustomerStatus,
  type UserSummary,
} from '../components/api'
import {
  Alert,
  Button,
  Card,
  FormField,
  Input,
  Select,
  Textarea,
} from '../components/ui'

type Mode = 'new' | 'edit'

type FormState = {
  company_name: string
  billing_name: string
  industry: string
  tags: string
  sales_user_id: string
  dev_user_id: string
  status: CustomerStatus
  notes: string
}

const initial: FormState = {
  company_name: '',
  billing_name: '',
  industry: '',
  tags: '',
  sales_user_id: '',
  dev_user_id: '',
  status: 'active',
  notes: '',
}

export function CustomerFormPage({ mode }: { mode: Mode }) {
  const params = useParams()
  const navigate = useNavigate()
  const id = params.id ? Number(params.id) : null

  const [form, setForm] = useState<FormState>(initial)
  const [salesUsers, setSalesUsers] = useState<UserSummary[]>([])
  const [devUsers, setDevUsers] = useState<UserSummary[]>([])
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [loading, setLoading] = useState(mode === 'edit')

  useEffect(() => {
    let cancelled = false
    Promise.all([
      api<{ items: UserSummary[] }>('/api/users', { query: { role: 'sales' } }),
      api<{ items: UserSummary[] }>('/api/users', { query: { role: 'dev' } }),
    ])
      .then(([sales, dev]) => {
        if (cancelled) return
        setSalesUsers(sales.items ?? [])
        setDevUsers(dev.items ?? [])
      })
      .catch(() => {
        /* 担当者リスト取得失敗は致命的ではない */
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (mode !== 'edit' || !id) return
    let cancelled = false
    setLoading(true)
    api<Customer>(`/api/customers/${id}`)
      .then((customer) => {
        if (cancelled) return
        setForm({
          company_name: customer.company_name,
          billing_name: customer.billing_name ?? '',
          industry: customer.industry ?? '',
          tags: customer.tags ?? '',
          sales_user_id: String(customer.sales_user_id),
          dev_user_id: String(customer.dev_user_id),
          status: customer.status,
          notes: customer.notes ?? '',
        })
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [mode, id])

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
    setFieldErrors((prev) => {
      if (!prev[key]) return prev
      const { [key]: _, ...rest } = prev
      return rest
    })
  }

  const validate = (): boolean => {
    const errors: Record<string, string> = {}
    if (!form.company_name.trim()) errors.company_name = '社名は必須です'
    if (!form.sales_user_id) errors.sales_user_id = '営業担当は必須です'
    if (!form.dev_user_id) errors.dev_user_id = '制作担当は必須です'
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!validate()) return
    setSubmitting(true)
    try {
      const body = {
        company_name: form.company_name.trim(),
        billing_name: form.billing_name.trim() || null,
        industry: form.industry.trim() || null,
        tags:
          form.tags
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean)
            .join(',') || null,
        sales_user_id: Number(form.sales_user_id),
        dev_user_id: Number(form.dev_user_id),
        status: form.status,
        notes: form.notes.trim() || null,
      }
      if (mode === 'edit' && id) {
        await api(`/api/customers/${id}`, { method: 'PUT', body })
        navigate(`/customers/${id}`, { replace: true })
      } else {
        const res = await api<{ id: number }>('/api/customers', {
          method: 'POST',
          body,
        })
        navigate(`/customers/${res.id}`, { replace: true })
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : '保存に失敗しました')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <p className="muted">読み込み中…</p>

  return (
    <>
      <div className="page-header">
        <h1>{mode === 'edit' ? '顧客を編集' : '顧客を新規登録'}</h1>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <form onSubmit={handleSubmit} noValidate>
        <Card title="基本情報">
          <div className="form-row">
            <FormField
              label="社名"
              htmlFor="company_name"
              required
              error={fieldErrors.company_name}
            >
              <Input
                id="company_name"
                value={form.company_name}
                onChange={(e) => update('company_name', e.target.value)}
                required
              />
            </FormField>
            <FormField label="請求名（同上なら空欄可）" htmlFor="billing_name">
              <Input
                id="billing_name"
                value={form.billing_name}
                onChange={(e) => update('billing_name', e.target.value)}
              />
            </FormField>
            <FormField label="業種" htmlFor="industry">
              <Input
                id="industry"
                value={form.industry}
                onChange={(e) => update('industry', e.target.value)}
                placeholder="医療 / 建設 / EC など"
              />
            </FormField>
            <FormField label="状態" htmlFor="status" required>
              <Select
                id="status"
                value={form.status}
                onChange={(e) =>
                  update('status', e.target.value as CustomerStatus)
                }
              >
                {(Object.keys(STATUS_LABEL) as CustomerStatus[]).map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABEL[s]}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField
              label="タグ"
              htmlFor="tags"
              hint="カンマ区切り（例：WordPress, EC, 月次更新）"
            >
              <Input
                id="tags"
                value={form.tags}
                onChange={(e) => update('tags', e.target.value)}
                placeholder="WordPress, EC"
              />
            </FormField>
          </div>
        </Card>

        <Card title="担当者">
          <div className="form-row">
            <FormField
              label="営業担当"
              htmlFor="sales_user_id"
              required
              error={fieldErrors.sales_user_id}
            >
              <Select
                id="sales_user_id"
                value={form.sales_user_id}
                onChange={(e) => update('sales_user_id', e.target.value)}
                required
              >
                <option value="">未選択</option>
                {salesUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}（{COMPANY_LABEL[u.company]}）
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField
              label="制作担当"
              htmlFor="dev_user_id"
              required
              error={fieldErrors.dev_user_id}
            >
              <Select
                id="dev_user_id"
                value={form.dev_user_id}
                onChange={(e) => update('dev_user_id', e.target.value)}
                required
              >
                <option value="">未選択</option>
                {devUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}（{COMPANY_LABEL[u.company]}）
                  </option>
                ))}
              </Select>
            </FormField>
          </div>
        </Card>

        <Card title="メモ">
          <FormField label="社内向けメモ" htmlFor="notes">
            <Textarea
              id="notes"
              value={form.notes}
              onChange={(e) => update('notes', e.target.value)}
              rows={5}
              placeholder="連絡時の注意点・特記事項など"
            />
          </FormField>
        </Card>

        <div className="form-actions">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate(-1)}
          >
            キャンセル
          </Button>
          <Button type="submit" variant="primary" disabled={submitting}>
            {submitting ? '保存中…' : '保存'}
          </Button>
        </div>
      </form>
    </>
  )
}
